import { useCallback, useEffect, useRef } from 'react';
import { cn } from '@rocco/utils/cn';
import { useTracker } from '@archipro-website/tracker';
import { useAuth } from '~/modules/root/context/AuthContext';
import type { LoginFlow } from '~/modules/user/hook/use-login-flow';

interface SpecifierLoginPromptProps {
    /** The professional's display name. */
    professionalName: string;
    /** The item description appended to the prompt copy. */
    itemDescription?: string;
    /** Optional additional CSS classes. */
    className?: string;
    /**
     * Show a bottom divider line. Defaults to `true`.
     * Disable when the next section renders its own top border.
     */
    showBottomBorder?: boolean;
}

/**
 * A prompt displayed to logged-out users when a professional has set their
 * enquiry audience to "Specifiers only". Provides links to sign up or log in.
 */
export const SpecifierLoginPrompt = ({
    professionalName,
    itemDescription = 'this product',
    className,
    showBottomBorder = true,
}: SpecifierLoginPromptProps) => {
    const { showAuthModal } = useAuth();
    const tracker = useTracker();

    // The prompt replaces the enquiry form entirely for gated professionals,
    // so its impression is a funnel step in its own right.
    const impressionLogged = useRef(false);
    useEffect(() => {
        if (impressionLogged.current) return;
        impressionLogged.current = true;
        tracker.log('LoginPromptShown', {
            url: new URL(window.location.href),
            data: {
                authSource: 'specifier',
                Source: 'specifier',
                ExtraData: JSON.stringify({ authSource: 'specifier' }),
            },
        });
    }, [tracker]);

    // authSource 'specifier' (not 'enquiry') so the modal-open event is
    // distinguishable from the post-enquiry email-fork prompts in analysis.
    const handleSignUp = useCallback(() => {
        showAuthModal({
            authSource: 'specifier',
            flow: 'signup' as LoginFlow,
        });
    }, [showAuthModal]);

    const handleLogIn = useCallback(() => {
        showAuthModal({
            authSource: 'specifier',
            flow: 'login' as LoginFlow,
        });
    }, [showAuthModal]);

    return (
        <div
            className={cn(
                'py-6',
                showBottomBorder && 'mb-px border-b border-black',
                className,
            )}
            data-testid="specifier-login-prompt"
        >
            <div className="border-charcoal-50 bg-warmWhite-25 rounded-md border p-5 shadow-xs">
                <p className="text-charcoal-250 text-base leading-snug">
                    Are you a specifier?{' '}
                    <button
                        type="button"
                        onClick={handleSignUp}
                        className="hover:text-charcoal-100 font-medium text-black underline"
                    >
                        Sign up
                    </button>{' '}
                    /{' '}
                    <button
                        type="button"
                        onClick={handleLogIn}
                        className="hover:text-charcoal-100 font-medium text-black underline"
                    >
                        log in
                    </button>{' '}
                    to enquire directly with {professionalName} about{' '}
                    {itemDescription}.
                </p>
            </div>
        </div>
    );
};
